import { useEffect, useMemo, useState } from "react";
import { JsonSchema, UISchemaElement } from "@jsonforms/core";
import { JsonForms } from "@jsonforms/react";
import { vanillaCells, vanillaRenderers } from "@jsonforms/vanilla-renderers";
import { Flex } from "styles/utility-components";

import { DefaultTheme } from "components/app_layout/DefaultTheme";
import AccountSearchControl from "components/form_components/json_forms/account_search/AccountSearchControl";
import AccountSearchTester from "components/form_components/json_forms/account_search/AccountSearchTester";
import DateControl from "components/form_components/json_forms/date/DateControl";
import DateControlTester from "components/form_components/json_forms/date/DateControlTester";
import EnumControl from "components/form_components/json_forms/enum/EnumControl";
import EnumControlTester from "components/form_components/json_forms/enum/EnumControlTester";
import NumberControl from "components/form_components/json_forms/number/NumberControl";
import NumberTester from "components/form_components/json_forms/number/NumberTester";
import OptionsControl from "components/form_components/json_forms/options/OptionsControl";
import OptionsTester from "components/form_components/json_forms/options/OptionsTester";
import OwnerSearchControl from "components/form_components/json_forms/owner_search/OwnerSearchControl";
import OwnerSearchTester from "components/form_components/json_forms/owner_search/OwnerSearchTester";
import {
  FormError,
  getRequiredFieldErrors,
} from "components/form_components/json_forms/requiredFieldErrors";
import StringControl from "components/form_components/json_forms/string/StringControl";
import StringControlTester from "components/form_components/json_forms/string/StringControlTester";
import TextAreaControl from "components/form_components/json_forms/textarea/TextAreaControl";
import TextAreaTester from "components/form_components/json_forms/textarea/TextAreaTester";
import { FormContext } from "components/form_components/json_forms/useFormDataContext";
import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import { OpportunityFormResponse } from "lib/generated/integration-api";
import { useOpportunityFormSchema } from "lib/hooks/api/integrations/useOpportunityFormSchema";
import { useSendOpportunity } from "lib/hooks/api/integrations/useSendOpportunity";
import { ExternalLink } from "lib/icons/ExternalLink";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { getFormattedDate } from "lib/utils";
import { IntegrationModalHeader } from "./IntegrationModalHeader";
import { SendToCRMButton } from "./ProviderButtons";
import { NativeProviderType } from "./providerMetadata";
import { getOpportunityHeaderCopy } from "./util";

import css from "./SendOpportunityToCRMModal.module.scss";

const renderers = [
  ...vanillaRenderers,
  { tester: StringControlTester, renderer: StringControl },
  { tester: EnumControlTester, renderer: EnumControl },
  { tester: OwnerSearchTester, renderer: OwnerSearchControl },
  { tester: DateControlTester, renderer: DateControl },
  { tester: AccountSearchTester, renderer: AccountSearchControl },
  { tester: OptionsTester, renderer: OptionsControl },
  { tester: TextAreaTester, renderer: TextAreaControl },
  { tester: NumberTester, renderer: NumberControl },
];

export type Opportunity = {
  guid: string;
  name: string;
  stotlesRoute: string;
  value?: number;
  createDate: string;
  closeDate?: string;
};

type SendOpportunityToCRMModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  opportunity: Opportunity;
  providerType: NativeProviderType;
};

// Setup field mappings - pre-populate fields if they are present
function initialFormData(formData: OpportunityFormResponse, opportunity: Opportunity) {
  const initialForm: Record<string, string | number> = {};
  for (const [key, val] of Object.entries(formData.mappings)) {
    switch (val) {
      case "title":
        initialForm[key] = opportunity.name;
        break;
      case "value":
        if (opportunity.value) {
          initialForm[key] = opportunity.value;
        }
        break;
      case "description":
        initialForm[key] = `Opportunity URL: ${window.location.origin}${opportunity.stotlesRoute}`;
        break;
      case "closeDate":
        if (opportunity.closeDate) {
          initialForm[key] = getFormattedDate(new Date(opportunity.closeDate));
        }
        break;
    }
  }

  return initialForm;
}

function cleanData(data: Record<string, unknown>): Record<string, unknown> {
  const newData = { ...data };
  for (const key of Object.keys(newData)) {
    if (newData[key] === undefined || newData[key] === null) {
      delete newData[key];
    }
  }
  return newData;
}

export function SendOpportunityToCRMModal({
  open,
  onOpenChange,
  opportunity,
  providerType,
}: SendOpportunityToCRMModalProps) {
  const { logEvent } = useTracking();
  const { mutate: sendOpportunity } = useSendOpportunity(
    providerType,
    opportunity.guid,
    opportunity.name,
    {
      onSuccess: () => {
        logEvent(EventNames.objectSentToCRM, trackingAttributes);
      },
    },
  );
  const [data, setData] = useState({});
  const { data: formData, isLoading } = useOpportunityFormSchema();
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const schema = useMemo<JsonSchema | undefined>(
    () => (formData ? (JSON.parse(formData.form) as JsonSchema) : undefined),
    [formData],
  );
  const uischema = useMemo<UISchemaElement | undefined>(
    () => (formData ? (JSON.parse(formData.layout) as UISchemaElement) : undefined),
    [formData],
  );

  // Required-field validation computed without AJV (see getRequiredFieldErrors).
  const additionalErrors = useMemo<FormError[]>(
    () => (schema ? getRequiredFieldErrors(schema, data) : []),
    [schema, data],
  );
  const hasErrors = additionalErrors.length > 0;

  const trackingAttributes = useMemo(() => {
    return {
      "Data types": "Opportunity",
      "Connected provider": providerType,
    };
  }, [providerType]);

  const onOpenChangeWrapper = (open: boolean, actionType: "submit" | "cancel" = "cancel") => {
    if (!open) {
      if (actionType === "submit") {
        sendOpportunity({ guid: opportunity.guid, data: cleanData(data) });
      } else {
        logEvent(EventNames.sendToCRMModalCancelled, trackingAttributes);
      }
    }
    onOpenChange(open);
  };

  const headerCopy = formData ? getOpportunityHeaderCopy(providerType, formData.entityName) : null;

  useEffect(() => {
    if (formData && !isFormInitialized) {
      setData(initialFormData(formData, opportunity));
      setIsFormInitialized(true);
    }
  }, [opportunity, formData, isFormInitialized]);

  return (
    <TrackingProvider
      data={{ "Context source": "Send to CRM modal", "Connected provider": providerType }}
    >
      <Modal open={open} onOpenChange={onOpenChangeWrapper}>
        <Flex column>
          {isLoading || !formData || !isFormInitialized ? (
            <>
              <SkeletonText width="100%" />
              <SkeletonText width="100%" />
              <SkeletonText width="100%" />
            </>
          ) : (
            <>
              <IntegrationModalHeader
                providerType={providerType}
                title={headerCopy?.title}
                subtitle={headerCopy?.subtitle}
              />
              <FormContext.Provider value={data}>
                <JsonForms
                  schema={schema}
                  data={data}
                  renderers={renderers}
                  uischema={uischema}
                  cells={vanillaCells}
                  // AJV compiles schemas with `new Function`, which the production CSP
                  // blocks ('unsafe-eval'). Disable AJV validation and supply our own
                  // required-field errors via `additionalErrors` instead.
                  validationMode="NoValidation"
                  additionalErrors={additionalErrors}
                  onChange={({ data }) => {
                    setData(data);
                  }}
                />
              </FormContext.Provider>
              <div className={css.btnContainer}>
                <Button
                  as="a"
                  buttonType="secondary"
                  onClick={() => onOpenChangeWrapper(false, "cancel")}
                  className={css.editFrom}
                  target="_blank"
                  // eslint-disable-next-line no-restricted-syntax -- opens integrations setup in a new tab so the in-progress send-to-CRM form isn't lost
                  href="/account-management/integrations/setup"
                >
                  Edit this form{" "}
                  <ExternalLink
                    className={css.externalLink}
                    fill={DefaultTheme.token?.colorTextBase}
                  />
                </Button>
                <SendToCRMButton
                  disabled={hasErrors}
                  onClick={() => onOpenChangeWrapper(false, "submit")}
                  providerType={providerType}
                  buttonType="primary"
                  eventName={EventNames.sendToCRMClicked}
                />
              </div>
            </>
          )}
        </Flex>
      </Modal>
    </TrackingProvider>
  );
}
